import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import HomePage from "@/components/HomePage";
import ProductsPage from "@/components/ProductsPage";
import CompanyPage from "@/components/CompanyPage";
import SolutionsPage from "@/components/SolutionsPage";
import ModelsPage from "@/components/ModelsPage";
import InfluencerLibraryPage from "@/components/InfluencerLibraryPage";
import LibraryPage from "@/components/LibraryPage";
import PricingPage from "@/components/PricingPage";
import Footer from "@/components/Footer";
import { Chatbot } from "@/components/Chatbot";
import type { PlanId } from "@/lib/pricing";
import { useHashScroll } from "@/lib/use-hash-scroll";
import {
  documentTitleForPath,
  normalizeSitePath,
  pathFromTab,
  tabFromPathname,
} from "@/lib/site-routes";

export default function IndexPage() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const normalizedPath = useMemo(() => normalizeSitePath(pathname), [pathname]);
  const activeTab = useMemo(() => tabFromPathname(normalizedPath), [normalizedPath]);
  useHashScroll();

  const setActiveTab = useCallback(
    (tab: string) => {
      const next = pathFromTab(tab);
      if (normalizeSitePath(next) === normalizedPath) return;
      navigate(next);
    },
    [normalizedPath, navigate],
  );
  const [currentPlanId, setCurrentPlanId] = useState<PlanId>("free");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [libraryFooterVisible, setLibraryFooterVisible] = useState(false);
  const [librarySubTab, setLibrarySubTab] = useState<"video" | "voice" | "model">("video");
  const [showFooterInSolution, setShowFooterInSolution] = useState(false);
  const [loginDialogTrigger, setLoginDialogTrigger] = useState(0);
  const [postLoginRedirect, setPostLoginRedirect] = useState<string | null>(null);

  const handleScrollToFooter = () => {
    setShowFooterInSolution(true);
    setTimeout(() => {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    }, 100);
  };

  const handleHideFooter = () => {
    setShowFooterInSolution(false);
  };

  useEffect(() => {
    if (activeTab !== "solution") {
      setShowFooterInSolution(false);
    }
  }, [activeTab]);

  // Sync ?tab= query param → librarySubTab (Inspiration dropdown deep-links into /library)
  useEffect(() => {
    if (activeTab !== "library") return;
    const tab = new URLSearchParams(search).get("tab");
    if (tab === "video" || tab === "voice" || tab === "model") {
      setLibrarySubTab(tab);
    }
  }, [activeTab, search]);

  useEffect(() => {
    document.title = documentTitleForPath(pathname);
  }, [pathname]);

  const isHeroView = activeTab === "home" || activeTab === "hero";

  const renderContent = () => {
    switch (activeTab) {
      case "models":
        return <ModelsPage />;
      case "creators":
        return (
          <InfluencerLibraryPage
            onRequestLogin={() => setLoginDialogTrigger((n) => n + 1)}
          />
        );
      case "products":
        return <ProductsPage onRequestLogin={(redirect) => {
              if (redirect) {
                setPostLoginRedirect(redirect);
                sessionStorage.setItem("loginRedirect", redirect);
              }
              setLoginDialogTrigger((n) => n + 1);
            }} />;
      case "company":
        return <CompanyPage />;
      case "solution":
        return <SolutionsPage />;
      case "pricing":
        return (
          <PricingPage
            currentPlanId={currentPlanId}
            setCurrentPlanId={setCurrentPlanId}
            onRequestLogin={() => setLoginDialogTrigger((n) => n + 1)}
          />
        );
      case "library":
        return <LibraryPage initialTab={librarySubTab} />;
      case "home":
      case "hero":
      default:
        return (
          <HomePage
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onScrollToFooter={handleScrollToFooter}
            onHideFooter={handleHideFooter}
            onRequestLogin={(redirect) => {
              if (redirect) {
                setPostLoginRedirect(redirect);
                sessionStorage.setItem("loginRedirect", redirect);
              }
              setLoginDialogTrigger((n) => n + 1);
            }}
          />
        );
    }
  };

  const isLibrary = activeTab === "library";
  const showFooter = isLibrary && libraryFooterVisible || !isLibrary;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        currentPlanId={currentPlanId}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isVisible={!isHeroView}
        setLibrarySubTab={setLibrarySubTab}
        loginDialogTrigger={loginDialogTrigger}
        postLoginRedirect={postLoginRedirect}
      />

      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="animate-fade-in flex-1">{renderContent()}</main>

      {showFooter && <Footer setActiveTab={setActiveTab} />}

      <Chatbot />
    </div>
  );
}
